import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, catchError, map, of } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface VoyageRequestItem {
  id: string;
  dbId?: number;
  clientName: string;
  vesselName: string;
  imo: string;
  vesselType: string;
  vesselEmail?: string;
  yearBuilt?: number;
  voyageRef?: string;
  startPort: string;
  startIsOthers?: boolean;
  startCustomPortName?: string;
  startUnlocode?: string;
  startLat?: number;
  startLatDir?: string;
  startLong?: number;
  startLongDir?: string;
  endPort: string;
  endIsOthers?: boolean;
  endCustomPortName?: string;
  endUnlocode?: string;
  endLat?: number;
  endLatDir?: string;
  endLong?: number;
  endLongDir?: string;
  departureDate: string;
  departureTime?: string;
  condition?: string;
  optimizationType: string;
  speed?: number;
  consumption?: number;
  speedRemarks?: string;
  avoidIce: boolean;
  avoidHRA: boolean;
  secaFuel: boolean;
  tct: boolean;
  dailyRate: string | number;
  etsPrice?: string | number;
  hsfoPrice?: number;
  lsmgoPrice?: number;
  vlsfoPrice?: number;
  biofuelPrice?: number;
  underKeel?: number;
  airDraft?: number;
  aftDraft?: number;
  forwardDraft?: number;
  meanDraft?: number;
  hazardDistance?: number;
  weatherRestriction?: boolean;
  maxWind?: number;
  maxWave?: number;
  distanceFromStop?: number;
  routingRemarks?: string;
  routeCsvName?: string;
  routeCsvSize?: string;
  routeCsvData?: string;
  routeCsvRowCount?: number;
  status: 'Draft' | 'Requested' | 'Resubmitted' | 'Under Review' | 'In Progress' | 'Edit Requested' | 'Created' | 'Completed' | 'Cancelled';
  statusHistory?: any[];
  submittedAt?: string;
  resolvedAt?: string;
  createdAt: string;
  updatedAt?: string;
  totalStopsCount?: number;
  speedSetsCount?: number;
  itineraryPortsCount?: number;
  itinerary?: any[];
  speedSets?: any[];
  fixedEtaParams?: any;
  rawPayload?: any;
  isDemo?: boolean;
  isExpanded?: boolean;
}

export interface RequestQueryParams {
  pageIndex: number;
  pageSize: number;
  tabMode?: 'active' | 'drafts' | 'all';
  clientFilter?: string;
  vesselFilter?: string;
  statusFilter?: string;
  searchTerm?: string;
  sortBy?: 'createdAt' | 'vesselName' | 'clientName' | 'status' | 'departureDate';
  sortOrder?: 'asc' | 'desc';
}

export interface RequestQueryResponse {
  data: VoyageRequestItem[];
  total: number;
  activeCount?: number;
  draftCount?: number;
  clients: string[];
  vessels: string[];
}

@Injectable({
  providedIn: 'root'
})
export class VoyageRequestsService {
  private readonly baseUrl = environment.apiBaseUrl;

  // Prefill state transfer for Initial Route Report
  private prefillSubject = new BehaviorSubject<any | null>(null);
  public prefillData$ = this.prefillSubject.asObservable();

  constructor(private http: HttpClient) {}

  public setPrefillForInitialRoute(data: any): void {
    this.prefillSubject.next(data);
  }

  public clearPrefillData(): void {
    this.prefillSubject.next(null);
  }

  public getPrefillData(): any | null {
    return this.prefillSubject.value;
  }

  /**
   * Fetch paginated and filtered voyage requests from backend
   */
  public queryRequests(
    params: RequestQueryParams,
    currentUser?: string,
    isMerakiOps?: boolean
  ): Observable<RequestQueryResponse> {
    let httpParams = new HttpParams()
      .set('pageIndex', params.pageIndex.toString())
      .set('pageSize', params.pageSize.toString())
      .set('sortBy', params.sortBy || 'createdAt')
      .set('sortOrder', params.sortOrder || 'desc');

    if (params.searchTerm) {
      httpParams = httpParams.set('searchTerm', params.searchTerm.trim());
    }
    if (params.clientFilter && params.clientFilter !== 'ALL') {
      httpParams = httpParams.set('clientFilter', params.clientFilter);
    }
    if (params.vesselFilter && params.vesselFilter !== 'ALL') {
      httpParams = httpParams.set('vesselFilter', params.vesselFilter);
    }
    if (params.statusFilter && params.statusFilter !== 'ALL') {
      httpParams = httpParams.set('statusFilter', params.statusFilter);
    }
    if (params.tabMode) {
      httpParams = httpParams.set('tabMode', params.tabMode);
    }

    const url = `${this.baseUrl}/scperformance/voyage-routing-requests`;
    return this.http.get<any>(url, { params: httpParams }).pipe(
      map(res => {
        if (res && res.success) {
          let items: VoyageRequestItem[] = res.data || [];
          if (!isMerakiOps && currentUser) {
            const uLower = currentUser.trim().toLowerCase();
            items = items.filter(
              r => {
                const cLower = (r.clientName || '').trim().toLowerCase();
                return cLower === uLower || cLower.includes(uLower) || uLower.includes(cLower);
              }
            );
          }
          return {
            data: items,
            total: res.total || items.length,
            activeCount: res.activeCount ?? 0,
            draftCount: res.draftCount ?? 0,
            clients: res.clients || [],
            vessels: res.vessels || []
          };
        }
        return { data: [], total: 0, clients: [], vessels: [] };
      }),
      catchError(err => {
        console.error('Error fetching voyage requests from backend:', err);
        return of({ data: [], total: 0, clients: [], vessels: [] });
      })
    );
  }

  /**
   * Create a new voyage routing request (draft or full submission)
   */
  public createRequest(formData: any): Observable<any> {
    const url = `${this.baseUrl}/scperformance/voyage-routing-request`;
    return this.http.post<any>(url, formData);
  }

  /**
   * Update an existing request (edits, draft submit, or requesting customer edits)
   */
  public updateRequest(requestId: string, formData: any): Observable<any> {
    const url = `${this.baseUrl}/scperformance/voyage-routing-request/${requestId}`;
    return this.http.put<any>(url, formData);
  }

  /**
   * Get single request details and timeline by ID
   */
  public getRequestById(requestId: string): Observable<any> {
    const url = `${this.baseUrl}/scperformance/voyage-routing-request/${requestId}`;
    return this.http.get<any>(url);
  }

  /**
   * Update request status
   */
  public updateStatus(requestId: string, newStatus: string, remarks?: string): Observable<any> {
    const url = `${this.baseUrl}/scperformance/voyage-routing-request/${requestId}/status`;
    return this.http.patch<any>(url, { status: newStatus, remarks });
  }

  /**
   * Log a milestone / action event to the request's timeline (e.g. PDF generation, download)
   */
  public logEvent(requestId: string, eventData: any): Observable<any> {
    const url = `${this.baseUrl}/scperformance/voyage-routing-request/${requestId}/event`;
    return this.http.post<any>(url, eventData);
  }

  /**
   * Fetch vessel's last reported arrival port from master_reports
   */
  public getLastPortByImo(imo: string | number): Observable<any> {
    const url = `${this.baseUrl}/scperformance/voyage-routing/last-port/${imo}`;
    return this.http.get<any>(url).pipe(
      catchError(err => {
        console.warn('Could not fetch last port for IMO:', imo, err);
        return of({ success: false, data: null });
      })
    );
  }

  /**
   * Calculate dynamic duration/time ago format:
   * - Unresolved: "Requested 2 hours ago", "Requested 15m ago"
   * - Resolved: "Resolved in 45 mins", "Resolved in 2 hours 15 mins"
   */
  public formatTimeAgoOrResolved(
    createdAt?: string,
    resolvedAt?: string | null,
    status?: string,
    statusHistory?: any[],
    submittedAt?: string | null
  ): { text: string; isResolved: boolean } {
    if (status === 'Draft') {
      return { text: 'Draft Saved', isResolved: false };
    }

    // Determine the LATEST submission / resubmission start time:
    let startTimeStr: string | undefined | null = null;
    if (Array.isArray(statusHistory) && statusHistory.length > 0) {
      for (let i = statusHistory.length - 1; i >= 0; i--) {
        const h = statusHistory[i];
        const act = h.action;
        const st = h.status;
        if (
          act === 'CUSTOMER_RESUBMITTED' ||
          act === 'RESUBMIT_EDIT' ||
          act === 'DRAFT_SUBMITTED' ||
          act === 'REQUEST_SUBMITTED' ||
          st === 'Resubmitted' ||
          (act === 'CREATED' && (st === 'Requested' || st === 'Resubmitted')) ||
          (st === 'Requested' && act !== 'CUSTOMER_EDIT_REQUESTED')
        ) {
          startTimeStr = h.timestamp;
          break;
        }
      }
    }

    if (!startTimeStr) {
      startTimeStr = submittedAt || createdAt;
    }

    if (!startTimeStr) return { text: '—', isResolved: false };

    const isResolved = ['Completed', 'Created', 'Cancelled'].includes(status || '');
    const startMs = new Date(startTimeStr).getTime();

    if (isResolved && resolvedAt) {
      const endMs = new Date(resolvedAt).getTime();
      const diffMs = Math.max(0, endMs - startMs);
      const diffMins = Math.round(diffMs / (1000 * 60));

      if (diffMins < 1) return { text: 'Resolved in < 1m', isResolved: true };
      if (diffMins < 60) return { text: `Resolved in ${diffMins}m`, isResolved: true };

      const hours = Math.floor(diffMins / 60);
      const remMins = diffMins % 60;
      if (hours < 24) {
        return { text: remMins > 0 ? `Resolved in ${hours}h ${remMins}m` : `Resolved in ${hours}h`, isResolved: true };
      }

      const days = Math.floor(hours / 24);
      const remHours = hours % 24;
      return { text: remHours > 0 ? `Resolved in ${days}d ${remHours}h` : `Resolved in ${days}d`, isResolved: true };
    }

    // Dynamic prefix based on status
    const prefix = status === 'Resubmitted' ? 'Resubmitted' : 'Requested';

    // Unresolved: Dynamic time ago from latest final submission
    const nowMs = Date.now();
    const diffMs = Math.max(0, nowMs - startMs);
    const diffSecs = Math.round(diffMs / 1000);
    const diffMins = Math.round(diffMs / (1000 * 60));

    if (diffSecs < 45) return { text: `${prefix} just now`, isResolved: false };
    if (diffMins < 60) return { text: `${prefix} ${diffMins}m ago`, isResolved: false };

    const hours = Math.floor(diffMins / 60);
    if (hours < 24) return { text: `${prefix} ${hours}h ago`, isResolved: false };

    const days = Math.floor(hours / 24);
    if (days < 7) return { text: `${prefix} ${days}d ago`, isResolved: false };

    const weeks = Math.floor(days / 7);
    return { text: `${prefix} ${weeks}w ago`, isResolved: false };
  }
}
